import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { LOCATIONS } from "@/lib/suppliers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, FileDown, Mail } from "lucide-react";
import { useT } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const STATUSES = ["Draft", "Sent to Accountant", "Paid via TFA"];
const QUARTERS = [1, 2, 3, 4];

const STATUS_STYLES = {
  "Draft":               "bg-slate-200 text-slate-800 border-slate-300",
  "Sent to Accountant":  "bg-amber-100 text-amber-800 border-amber-200",
  "Paid via TFA":        "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function TaxMatrixTable({ refreshKey = 0 }) {
  const t = useT();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [store, setStore] = useState("Global");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // quarter number while busy
  const [pdfBusy, setPdfBusy] = useState(null); // quarter number while busy
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [form4, setForm4] = useState(null);
  const [income, setIncome] = useState(null);
  // Share via Email (Gmail SMTP) state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailQuarter, setEmailQuarter] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const loadTfa = async () => {
    try {
      const params = { year, quarter: selectedQuarter };
      if (store !== "Global") params.store = store;
      const { data } = await api.get("/portal/tax-form4", { params });
      setForm4(data);
    } catch {
      setForm4(null);
    }
    try {
      const params2 = { year };
      if (store !== "Global") params2.store = store;
      const { data } = await api.get("/portal/income-tax", { params: params2 });
      setIncome(data);
    } catch {
      setIncome(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (store !== "Global") params.store = store;
      const { data } = await api.get("/portal/tax-matrix", { params });
      setRows(data.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadTfa();
    const i = setInterval(() => { load(); loadTfa(); }, 30000); // live recalc every 30 s
    return () => clearInterval(i);
  }, [year, store, selectedQuarter, refreshKey]);

  const updateStatus = async (quarter, status) => {
    try {
      await api.patch("/portal/tax-matrix/status", { year, quarter, status, store });
      toast.success(`${store} · Q${quarter} ${year} → ${status}`);
      setRows((rs) => rs.map((r) => (r.quarter === quarter ? { ...r, status } : r)));
    } catch {
      toast.error("Could not save status");
    }
  };

  const exportQuarter = async (quarter) => {
    setExporting(quarter);
    const t = toast.loading(`Compiling Q${quarter} ${year} · ${store} tax package…`);
    try {
      const url = new URL(`${API}/portal/tax-export`);
      url.searchParams.set("quarter", String(quarter));
      url.searchParams.set("year", String(year));
      if (store !== "Global") url.searchParams.set("store", store);
      const resp = await fetch(url.toString(), {
        headers: { Authorization: "Bearer savvas:savvas" },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m
        ? m[1]
        : `Quarterly_Tax_Report_Q${quarter}_${year}${store !== "Global" ? "_" + store : ""}.zip`;
      const blob = await resp.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`Saved · ${filename}`, { id: t });
    } catch (e) {
      toast.error(e?.message || "Export failed", { id: t });
    } finally {
      setExporting(null);
    }
  };

  const downloadTfaPdf = async (quarter) => {
    setPdfBusy(quarter);
    const t = toast.loading(`Generating Cyprus TFA Form 4 PDF · Q${quarter} ${year} · ${store}…`);
    try {
      const url = new URL(`${API}/portal/tax-pdf`);
      url.searchParams.set("quarter", String(quarter));
      url.searchParams.set("year", String(year));
      if (store !== "Global") url.searchParams.set("store", store);
      const resp = await fetch(url.toString(), {
        headers: { Authorization: "Bearer savvas:savvas" },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m
        ? m[1]
        : `Cyprus_Tax_Report_Q${quarter}_${year}${store !== "Global" ? "_" + store : ""}.pdf`;
      const blob = await resp.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`Saved · ${filename}`, { id: t });
    } catch (e) {
      toast.error(e?.message || "PDF export failed", { id: t });
    } finally {
      setPdfBusy(null);
    }
  };

  const openEmailDialog = (quarter) => {
    const storeLabel = store === "Global" ? "Global · All Kiosks" : store;
    setEmailQuarter(quarter);
    setEmailSubject(`Cyprus TFA Form 4 · Q${quarter} ${year} · ${storeLabel}`);
    setEmailMessage(
      `Attached is the Cyprus TFA (Tax For All) Form 4 VAT Return PDF\n` +
      `for ${storeLabel} · Q${quarter} ${year}.\n\n` +
      `This document is auto-generated by KioskForce ERP. Please verify\n` +
      `with your accountant before submission to the Cyprus tax portal.\n\n` +
      `— KioskForce ERP`
    );
    setEmailOpen(true);
  };

  const sendTaxEmail = async () => {
    if (!emailQuarter) return;
    const recipients = (emailTo || "")
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter((s) => s && s.includes("@"));
    if (recipients.length === 0) {
      toast.error("Please enter at least one valid email address.");
      return;
    }
    setEmailSending(true);
    const t = toast.loading(`Sending TFA PDF to ${recipients.length} recipient${recipients.length > 1 ? "s" : ""}…`);
    try {
      const payload = {
        year, quarter: emailQuarter, to_emails: recipients,
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
      };
      if (store !== "Global") payload.store = store;
      const resp = await fetch(`${API}/portal/tax-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer savvas:savvas",
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      toast.success(`Sent · ${data.filename} → ${recipients.join(", ")}`, { id: t });
      setEmailOpen(false);
      setEmailTo("");
    } catch (e) {
      toast.error(e?.message || "Email send failed", { id: t });
    } finally {
      setEmailSending(false);
    }
  };

  const fmt = (n) =>
    `€${Number(n || 0).toLocaleString("en-CY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Card className="p-5" data-testid="tax-matrix">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-lg leading-tight flex items-center gap-2">
            <span>📊</span> Quarterly VAT &amp; Tax Matrix
            <Badge
              className={
                store === "Global"
                  ? "bg-slate-900 text-white border-slate-900 ml-2"
                  : "bg-indigo-100 text-indigo-800 border-indigo-200 ml-2"
              }
              data-testid="tax-matrix-mode-badge"
            >
              {store === "Global" ? "GLOBAL · ΟΛΑ" : store.toUpperCase()}
            </Badge>
          </h3>
          <p className="text-[11px] text-slate-500 tracking-wide">
            Dual-mode · {store === "Global" ? "all 4 kiosks aggregated" : `${store} only`} · Cyprus TFA-ready totals
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger className="w-[180px]" data-testid="tax-matrix-store">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Global" data-testid="tax-matrix-store-global">Global · Όλα</SelectItem>
              {LOCATIONS.map((l) => (
                <SelectItem key={l} value={l} data-testid={`tax-matrix-store-${l.toLowerCase()}`}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[110px]" data-testid="tax-matrix-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* === Cyprus TFA Form 4 + Corporate Tax Estimator ============== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5" data-testid="tfa-suite">
        {/* Form 4 panel */}
        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold" data-testid="tfa-form4-title">
                {t("common.taxForm4")}
              </div>
              <div className="text-[11px] text-slate-500">
                {form4 ? `${form4.period_start} → ${form4.period_end}` : "—"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(selectedQuarter)} onValueChange={(v) => setSelectedQuarter(Number(v))}>
                <SelectTrigger className="w-[110px] h-9" data-testid="tfa-form4-quarter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUARTERS.map((q) => (
                    <SelectItem key={q} value={String(q)} data-testid={`tfa-form4-q-${q}`}>
                      Q{q}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => downloadTfaPdf(selectedQuarter)}
                disabled={pdfBusy === selectedQuarter}
                className="h-9 gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="tfa-pdf-generate"
                title="Generate Official Tax-Ready PDF (TFA)"
              >
                <FileText className="h-4 w-4" />
                <span className="text-[11px] font-bold">
                  {pdfBusy === selectedQuarter ? "…" : "Generate TFA PDF"}
                </span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openEmailDialog(selectedQuarter)}
                className="h-9 gap-2 border-slate-900 text-slate-900 hover:bg-slate-50"
                data-testid="tfa-pdf-email"
                title="Email Cyprus TFA PDF to accountant (Gmail SMTP)"
              >
                <Mail className="h-4 w-4" />
                <span className="text-[11px] font-bold">
                  Email · Αποστολή
                </span>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
            <div className="rounded-md bg-white border border-slate-200 p-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Box 1 · 5%</div>
              <div className="font-mono text-base text-slate-900" data-testid="tfa-box1-5">
                {fmt(form4?.boxes?.box1_output_vat_5)}
              </div>
              <div className="text-[9px] text-slate-400">Output VAT @ 5%</div>
            </div>
            <div className="rounded-md bg-white border border-slate-200 p-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Box 1 · 19%</div>
              <div className="font-mono text-base text-slate-900" data-testid="tfa-box1-19">
                {fmt(form4?.boxes?.box1_output_vat_19)}
              </div>
              <div className="text-[9px] text-slate-400">Output VAT @ 19%</div>
            </div>
            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2">
              <div className="text-[9px] uppercase tracking-wider text-indigo-700 font-bold">Box 4 · Total Output</div>
              <div className="font-mono text-base font-bold text-indigo-900" data-testid="tfa-box4">
                {fmt(form4?.boxes?.box4_total_output_vat)}
              </div>
              <div className="text-[9px] text-indigo-500">ΦΠΑ Πωλήσεων</div>
            </div>
            <div className="rounded-md bg-sky-50 border border-sky-200 p-2">
              <div className="text-[9px] uppercase tracking-wider text-sky-700 font-bold">Box 7 · Total Input</div>
              <div className="font-mono text-base font-bold text-sky-900" data-testid="tfa-box7">
                {fmt(form4?.boxes?.box7_total_input_vat)}
              </div>
              <div className="text-[9px] text-sky-500">ΦΠΑ Εξόδων</div>
            </div>
            {(() => {
              const net = Number(form4?.boxes?.box9_net_vat || 0);
              const positive = net > 0;
              const negative = net < 0;
              const bg = positive ? "bg-red-50 border-red-200"
                : negative ? "bg-emerald-50 border-emerald-200"
                : "bg-slate-50 border-slate-200";
              const txt = positive ? "text-red-700"
                : negative ? "text-emerald-700"
                : "text-slate-700";
              return (
                <div className={`rounded-md border p-2 ${bg}`}>
                  <div className={`text-[9px] uppercase tracking-wider font-bold ${txt}`}>
                    Box 9 · Net VAT
                  </div>
                  <div className={`font-mono text-base font-bold ${txt}`} data-testid="tfa-box9">
                    {positive ? "+" : ""}{fmt(net)}
                  </div>
                  <div className={`text-[9px] font-bold uppercase tracking-wider ${txt}`}>
                    {form4?.boxes?.box9_label || "—"}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Corporate Tax Estimator */}
        <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 p-4 text-white">
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">
            Cyprus Corporate Tax · 15%
          </div>
          <div className="text-[11px] text-slate-400 mb-3">
            Φόρος Εταιρειών · {income?.year || year} {income?.store ? `· ${income.store}` : ""}
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-300">Gross Revenue</span>
              <span className="font-mono" data-testid="corp-gross">{fmt(income?.gross_revenue)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">– Deductible OpEx</span>
              <span className="font-mono" data-testid="corp-opex">{fmt(income?.deductible_opex)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-700 pt-1.5">
              <span className="font-bold">Net Profit</span>
              <span className="font-mono font-bold" data-testid="corp-net">{fmt(income?.net_profit)}</span>
            </div>
            <div className="flex justify-between text-red-300">
              <span className="font-bold">Annual Tax @ 15%</span>
              <span className="font-mono font-bold" data-testid="corp-annual">{fmt(income?.corporate_tax_annual)}</span>
            </div>
            <div className="flex justify-between text-amber-300 text-[12px]">
              <span>→ Quarterly provision</span>
              <span className="font-mono" data-testid="corp-quarterly">{fmt(income?.quarterly_provision)}</span>
            </div>
          </div>
          <div className="mt-3 text-[10px] text-slate-400 italic">
            Auto-estimated · Verify with your accountant.
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="tax-matrix-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
              <th className="py-2 pr-3">Quarter</th>
              <th className="py-2 pr-3 text-right">Gross Sales</th>
              <th className="py-2 pr-3 text-right">Output VAT</th>
              <th className="py-2 pr-3 text-right">Input VAT</th>
              <th className="py-2 pr-3 text-right">Net VAT</th>
              <th className="py-2 pr-3 text-right">Deductible OpEx</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3 text-center w-12">USB</th>
              <th className="py-2 pr-3 text-center w-12">PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const net = Number(r.net_vat_due || 0);
              const netColor =
                net > 0 ? "text-red-700"      // payable to State
                : net < 0 ? "text-emerald-700" // refund due
                : "text-slate-500";
              return (
                <tr key={r.quarter} className="border-b border-slate-100" data-testid={`tax-matrix-q${r.quarter}`}>
                  <td className="py-2 pr-3 font-bold text-slate-900">
                    {r.label}
                    <div className="text-[10px] font-mono font-normal text-slate-400">
                      {r.period_start} → {r.period_end}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">{fmt(r.gross_sales)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{fmt(r.output_vat)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{fmt(r.input_vat)}</td>
                  <td className={`py-2 pr-3 text-right font-mono font-bold ${netColor}`}
                      data-testid={`tax-matrix-net-q${r.quarter}`}>
                    {net > 0 ? "+" : ""}{fmt(net)}
                    {net > 0 && <div className="text-[9px] font-normal uppercase tracking-wider opacity-75">owed to state</div>}
                    {net < 0 && <div className="text-[9px] font-normal uppercase tracking-wider opacity-75">refund due</div>}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-slate-700">{fmt(r.deductible_opex)}</td>
                  <td className="py-2 pr-3">
                    <Select
                      value={r.status || "Draft"}
                      onValueChange={(v) => updateStatus(r.quarter, v)}
                    >
                      <SelectTrigger
                        className={`min-w-[160px] h-8 text-xs ${STATUS_STYLES[r.status || "Draft"]}`}
                        data-testid={`tax-matrix-status-q${r.quarter}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 pr-3 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => exportQuarter(r.quarter)}
                      disabled={exporting === r.quarter}
                      className="h-8 px-2 gap-1"
                      data-testid={`tax-matrix-export-q${r.quarter}`}
                      title={`Export Q${r.quarter} ${year} · ${store} tax package`}
                    >
                      <Download className="h-3.5 w-3.5" />
                      <span className="text-[10px]">{exporting === r.quarter ? "…" : "ZIP"}</span>
                    </Button>
                  </td>
                  <td className="py-2 pr-3 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadTfaPdf(r.quarter)}
                      disabled={pdfBusy === r.quarter}
                      className="h-8 px-2 gap-1"
                      data-testid={`tax-matrix-pdf-q${r.quarter}`}
                      title={`Generate Cyprus TFA PDF · Q${r.quarter} ${year} · ${store}`}
                    >
                      <FileDown className="h-3.5 w-3.5" />
                      <span className="text-[10px]">{pdfBusy === r.quarter ? "…" : "PDF"}</span>
                    </Button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="text-center text-slate-400 py-6">
                  No data yet — start running shifts to populate this matrix.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-600" />Owed to State</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-600" />Refund / Credit</span>
        <span className="ml-auto font-mono">Auto-refresh · 30s</span>
      </div>

      <Dialog open={emailOpen} onOpenChange={(o) => !emailSending && setEmailOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="tfa-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              <span>Email Cyprus TFA PDF · Αποστολή Φόρου</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Sends the official Cyprus TFA Form 4 PDF for Q{emailQuarter} {year}
              {store !== "Global" && ` · ${store}`} via Gmail SMTP.
              Separate multiple recipients with commas.
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Recipients · Παραλήπτες
              </Label>
              <Input
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="accountant@example.com, owner@example.com"
                data-testid="tfa-email-to"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Subject · Θέμα
              </Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                data-testid="tfa-email-subject"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Message · Μήνυμα
              </Label>
              <Textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={6}
                data-testid="tfa-email-message"
                className="mt-1 font-mono text-[12px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailOpen(false)}
              disabled={emailSending}
              data-testid="tfa-email-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={sendTaxEmail}
              disabled={emailSending}
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
              data-testid="tfa-email-send"
            >
              <Mail className="h-4 w-4" />
              {emailSending ? "Sending…" : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
